import Input from "components/common/input";
import Check from "components/common/check";
import AsyncSelect from "components/common/select/AsyncSelect";
import {
  approvalConfigrationInitialValues,
  approvalConfigrationSchema,
} from "constants/approvalConfigration";
import { ErrorMessage, FieldArray, Formik } from "formik";
import React, { useEffect, useState, useMemo } from "react";
import { CaretDown, CaretRight, Plus, Trash } from "phosphor-react";
import { getCounties, getStates } from "containers/policies/api";
import { initialMetaForTable } from "constants/common";
import {
  useDebouncedCountiesDataCallback,
  useDebouncedStatesDataCallback,
  useDebouncedAccountingDataCallback,
  useDebouncedExecutiveDataCallback,
} from "utils/debouncedHelper";
import { getUserByType } from "containers/users/api";
import {
  createApprovalConfiguration,
  updateApprovalConfiguration,
} from "containers/configuration/api";
import { capitalizeName, sortData } from "utils/common";
import ToggleButton from "components/common/togglebutton";

const AddApprovalConfigrationForm = ({
  handleClose,
  editId,
  editData,
  onSuccess,
}) => {
  const [expandedRows, setExpandedRows] = useState({ approver: {} });
  const [statesList, setStatesList] = useState([]);
  const [countiesList, setCountiesList] = useState({});
  const [accountingList, setAccountingList] = useState([]);
  const [executiveList, setExecutiveList] = useState([]);

  const debounceAccountingDataCallBack = useDebouncedAccountingDataCallback();
  const debounceExecutiveDataCallBack = useDebouncedExecutiveDataCallback();
  const debouncedStatesDataCallback = useDebouncedStatesDataCallback();
  const debouncedCoutiesDataCallback = useDebouncedCountiesDataCallback();

  const toggleRow = (type, index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [type]: { ...prev[type], [index]: !prev[type][index] },
    }));
  };

  const fetchStates = async () => {
    try {
      const response = await getStates(initialMetaForTable);
      if (response?.states) {
        setStatesList(sortData(response.states));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCounties = async (stateId, index) => {
    if (!stateId) return;
    try {
      const response = await getCounties({
        ...initialMetaForTable,
        state_id: stateId,
      });
      if (response?.counties) {
        setCountiesList((prev) => ({
          ...prev,
          [index]: sortData(response.counties),
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const [accounting, executive] = await Promise.all([
        getUserByType({ ...initialMetaForTable, user_type: "accounting" }),
        getUserByType({ ...initialMetaForTable, user_type: "executive" }),
      ]);

      setAccountingList(sortData(accounting?.accountings) || []);
      setExecutiveList(sortData(executive?.executives) || []);
    };

    loadData();
    fetchStates();
  }, []);

  // Load counties when editing
  useEffect(() => {
    if (editData?.approval_program_coordinators?.length) {
      editData.approval_program_coordinators.forEach((item, idx) => {
        if (item.state_id) fetchCounties(item.state_id, idx);
        setExpandedRows((prev) => ({
          ...prev,
          approver: { ...prev.approver, [idx]: true },
        }));
      });
    }
  }, [editData]);

  const formInitialValues = useMemo(() => {
    if (!editData) return approvalConfigrationInitialValues;

    const activeApprovers = (
      editData.approval_program_coordinators || []
    ).filter((item) => !item._destroy && item.id);

    return {
      status: editData.status || "active",
      payment_method_name: editData.approval_method?.trim() || "",
      cs_manager_approvel: editData.cs_manager_required || false,
      executive_approvel_enable: editData.executive_approval_required || false,
      executive_approvel_email: editData.executive_email || "",
      accounting_approvel_enable:
        editData.accounting_approval_required || false,
      accounting_approvel_email: editData.accounting_approval_email || "",
      program_coordinator_approvel:
        editData.program_coordinator_required || false,
      designated_approver: activeApprovers.map((item) => ({
        id: item.id,
        designated_approver_state: item.state_id || "",
        designated_approver_state_name: item.state?.name || "",
        designated_approver_county: item.county_id || "",
        designated_approver_county_name: item.county?.name || "",
        designated_approver_email: item.email || "",
        _destroy: false,
      })),
    };
  }, [editData]);

  const approvalPayload = (values) => {
    const approversAttributes = values.designated_approver.map((item) => ({
      id: item.id || null,
      email: item.designated_approver_email || null,
      state_id: item.designated_approver_state || null,
      county_id: item.designated_approver_county || null,
      _destroy: item._destroy || false,
    }));

    return {
      approval_configuration: {
        status: values.status,
        approval_method: values.payment_method_name,
        cs_manager_required: values.cs_manager_approvel,
        executive_approval_required: values.executive_approvel_enable,
        executive_email: values.executive_approvel_email || null,
        accounting_approval_required: values.accounting_approvel_enable,
        accounting_approval_email: values.accounting_approvel_email || null,
        program_coordinator_required: values.program_coordinator_approvel,
        approval_program_coordinators_attributes: approversAttributes,
      },
    };
  };

  return (
    <Formik
      initialValues={formInitialValues}
      enableReinitialize
      validationSchema={approvalConfigrationSchema}
      onSubmit={async (values) => {
        try {
          const payload = approvalPayload(values);
          const response = editId
            ? await updateApprovalConfiguration(payload, editId)
            : await createApprovalConfiguration(payload);

          if (response) {
            onSuccess?.();
            handleClose();
          }
        } catch (error) {
          console.log("Submit error:", error);
        }
      }}
    >
      {({ handleChange, values, handleSubmit, setFieldValue }) => (
        <div className="question-modal">
          <form className="form-main" onSubmit={handleSubmit}>
            <hr className="custom-modal-line mt-0" />
            <ToggleButton
              label="Status"
              name="status"
              value={values.status === "active" ? 1 : 0}
              handleChange={(_, val) =>
                setFieldValue("status", val ? "active" : "inactive")
              }
            />

            <Input
              label="Payment Method Name"
              isRequired
              placeholder="e.g. Check"
              type="text"
              value={values.payment_method_name}
              name="payment_method_name"
              handleChange={handleChange}
              error={
                <ErrorMessage
                  className="error-message"
                  component="p"
                  name="payment_method_name"
                />
              }
            />
            <Check
              name="program_coordinator_approvel"
              checked={values.program_coordinator_approvel}
              label="Program Coordinator Approval Required?"
              handleChange={() =>
                setFieldValue(
                  "program_coordinator_approvel",
                  !values.program_coordinator_approvel,
                )
              }
            />

            {values.program_coordinator_approvel && (
              <div>
                <div className="fs_20 py-2">Designated Approver</div>
                <FieldArray name="designated_approver">
                  {({ push, remove }) => (
                    <div className="background p-3">
                      {values.designated_approver
                        .filter((a) => !a._destroy)
                        .map((approver, visibleIndex) => {
                          const originalIndex = values.designated_approver.findIndex(
                            (item) => item === approver,
                          );
                          const selectedCounty = (
                            countiesList[originalIndex] || []
                          ).find(
                            (c) => c.id === approver.designated_approver_county,
                          );
                          const selectedState = statesList.find(
                            (s) => s.id === approver.designated_approver_state,
                          );

                          return (
                            <div
                              key={originalIndex}
                              className="mb-3 border-bottom pb-3"
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex gap-2 align-items-center">
                                  {expandedRows.approver[originalIndex] ? (
                                    <CaretDown
                                      size={20}
                                      className="cursor-pointer"
                                      onClick={() =>
                                        toggleRow("approver", originalIndex)
                                      }
                                    />
                                  ) : (
                                    <CaretRight
                                      size={20}
                                      className="cursor-pointer opacity-50"
                                      onClick={() =>
                                        toggleRow("approver", originalIndex)
                                      }
                                    />
                                  )}
                                  <div
                                    className={`heading-smb ${
                                      !expandedRows.approver[originalIndex]
                                        ? "opacity-75"
                                        : "opacity-100"
                                    }`}
                                  >
                                    Approver {visibleIndex + 1}
                                  </div>
                                </div>
                                <Trash
                                  size={20}
                                  className="cursor-pointer opacity-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (approver.id) {
                                      setFieldValue(
                                        `designated_approver[${originalIndex}]._destroy`,
                                        true,
                                      );
                                    } else {
                                      remove(originalIndex);
                                    }
                                  }}
                                />
                              </div>

                              {expandedRows.approver[originalIndex] && (
                                <div className="row mt-3">
                                  <div className="col-sm-4">
                                    <AsyncSelect
                                      label="State"
                                      isRequired
                                      options={statesList.map((item) => ({
                                        label: item.name.toUpperCase(),
                                        value: item.id,
                                      }))}
                                      selectedValue={
                                        selectedState
                                          ? {
                                              label: selectedState.name.toUpperCase(),
                                              value: selectedState.id,
                                            }
                                          : approver.designated_approver_state
                                          ? {
                                              label:
                                                approver.designated_approver_state_name ||
                                                String(
                                                  approver.designated_approver_state,
                                                ),
                                              value:
                                                approver.designated_approver_state,
                                            }
                                          : null
                                      }
                                      placeholder="Select"
                                      handleChange={(obj) => {
                                        setFieldValue(
                                          `designated_approver[${originalIndex}].designated_approver_state`,
                                          obj.value,
                                        );
                                        setFieldValue(
                                          `designated_approver[${originalIndex}].designated_approver_state_name`,
                                          obj.label,
                                        );

                                        fetchCounties(obj.value, originalIndex);

                                        setFieldValue(
                                          `designated_approver[${originalIndex}].designated_approver_county`,
                                          "",
                                        );
                                        setFieldValue(
                                          `designated_approver[${originalIndex}].designated_approver_county_name`,
                                          "",
                                        );
                                      }}
                                      loadOptions={(inputValue) =>
                                        new Promise((resolve) =>
                                          debouncedStatesDataCallback(
                                            inputValue,
                                            resolve,
                                          ),
                                        )
                                      }
                                      error={
                                        <ErrorMessage
                                          className="error-message"
                                          component="p"
                                          name={`designated_approver[${originalIndex}].designated_approver_state`}
                                        />
                                      }
                                    />
                                  </div>
                                  <div className="col-sm-4">
                                    <AsyncSelect
                                      label="County"
                                      isRequired
                                      options={(
                                        countiesList[originalIndex] || []
                                      ).map((item) => ({
                                        label: capitalizeName(item.name),
                                        value: item.id,
                                      }))}
                                      selectedValue={
                                        selectedCounty
                                          ? {
                                              label: capitalizeName(
                                                selectedCounty.name,
                                              ),
                                              value: selectedCounty.id,
                                            }
                                          : approver.designated_approver_county
                                          ? {
                                              label:
                                                approver.designated_approver_county_name ||
                                                String(
                                                  approver.designated_approver_county,
                                                ),
                                              value:
                                                approver.designated_approver_county,
                                            }
                                          : null
                                      }
                                      placeholder="Select"
                                      handleChange={(obj) => {
                                        setFieldValue(
                                          `designated_approver[${originalIndex}].designated_approver_county`,
                                          obj.value,
                                        );
                                        setFieldValue(
                                          `designated_approver[${originalIndex}].designated_approver_county_name`,
                                          obj.label,
                                        );
                                      }}
                                      loadOptions={(inputValue) =>
                                        new Promise((resolve) =>
                                          debouncedCoutiesDataCallback(
                                            inputValue,
                                            approver.designated_approver_state,
                                            resolve,
                                          ),
                                        )
                                      }
                                      error={
                                        <ErrorMessage
                                          className="error-message"
                                          component="p"
                                          name={`designated_approver[${originalIndex}].designated_approver_county`}
                                        />
                                      }
                                    />
                                  </div>
                                  <div className="col-sm-4">
                                    <Input
                                      label="Email"
                                      isRequired
                                      placeholder="e.g. info@ifhomeless.org"
                                      type="email"
                                      value={approver.designated_approver_email}
                                      name={`designated_approver[${originalIndex}].designated_approver_email`}
                                      handleChange={handleChange}
                                      error={
                                        <ErrorMessage
                                          className="error-message"
                                          component="p"
                                          name={`designated_approver[${originalIndex}].designated_approver_email`}
                                        />
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                      <button
                        type="button"
                        className="secondary-btn w-100 mt-3"
                        onClick={() =>
                          push({
                            designated_approver_state: "",
                            designated_approver_state_name: "",
                            designated_approver_county: "",
                            designated_approver_county_name: "",
                            designated_approver_email: "",
                            _destroy: false,
                          })
                        }
                      >
                        <Plus size={18} /> Add More
                      </button>
                    </div>
                  )}
                </FieldArray>
              </div>
            )}
            <Check
              name="cs_manager_approvel"
              checked={values.cs_manager_approvel}
              label="CS Manager Approval Required?"
              handleChange={() =>
                setFieldValue(
                  "cs_manager_approvel",
                  !values.cs_manager_approvel,
                )
              }
            />
            <Check
              name="executive_approvel_enable"
              checked={values.executive_approvel_enable}
              label="Executive Approval Required?"
              handleChange={() =>
                setFieldValue(
                  "executive_approvel_enable",
                  !values.executive_approvel_enable,
                )
              }
            />

            {values.executive_approvel_enable && (
              <AsyncSelect
                label="Executive Email"
                isRequired
                placeholder="mary.smith@ifhomeless.org"
                options={executiveList.map((item) => ({
                  label: item.full_name,
                  value: item.email,
                }))}
                loadOptions={(inputValue) =>
                  new Promise((resolve) =>
                    debounceExecutiveDataCallBack(
                      inputValue,
                      "executive",
                      resolve,
                    ),
                  )
                }
                selectedValue={values.executive_approvel_email}
                handleChange={(obj) =>
                  setFieldValue("executive_approvel_email", obj.value)
                }
              />
            )}
            <Check
              name="accounting_approvel_enable"
              checked={values.accounting_approvel_enable}
              label="Accounting Approval Required?"
              handleChange={() =>
                setFieldValue(
                  "accounting_approvel_enable",
                  !values.accounting_approvel_enable,
                )
              }
            />

            {values.accounting_approvel_enable && (
              <AsyncSelect
                label="Accounting Email"
                isRequired
                placeholder="mary.smith@ifhomeless.org"
                options={accountingList.map((item) => ({
                  label: item.full_name,
                  value: item.email,
                }))}
                loadOptions={(inputValue) =>
                  new Promise((resolve) =>
                    debounceAccountingDataCallBack(
                      inputValue,
                      "accounting",
                      resolve,
                    ),
                  )
                }
                selectedValue={values.accounting_approvel_email}
                handleChange={(obj) =>
                  setFieldValue("accounting_approvel_email", obj.value)
                }
              />
            )}

            <div className="action gap-2 mt-4">
              <button
                className="secondary-btn w-50"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button className="primary-btn w-50" type="submit">
                {editId ? "Edit Payment Method" : "Add Payment Method"}
              </button>
            </div>
          </form>
        </div>
      )}
    </Formik>
  );
};

export default AddApprovalConfigrationForm;
